import type { ContainsGroup, ItemStat, LeadHint, Meal, OverlapLabel, PatternCandidate, SuspectOverlap, Symptom } from './types';
import { getMealGroups, getMealIngredients } from './ingredientTaxonomy';
import { mealFollowedByFlare, type FlareContext } from './insightStats';
import { makePatternCounter, mealGroupCarriers } from './insightVerify';

// Lead overlaps, pair labels, pattern candidates and lead hints for the insight brief.

const OVERLAP_CAP = 12;

/**
 * Meal counts for every pair of leads: the top ingredient leads plus the groups that have meals.
 * A meal "has" a name when one of its raw ingredients is that name or its groups include it.
 * Pairs where an ingredient always carries the group (e.g. a dairy member and "dairy") are skipped:
 * they overlap by definition and say nothing about confounding.
 * For a pair of two groups, a meal where one ingredient carries both (soy sauce: gluten + soy) is
 * explained by one food, not a combination, so it is left out of the pair's counts; a pair whose
 * shared meals all have such an ingredient is dropped (the carrier is judged on its own as an
 * ingredient lead).
 * Kept: pairs eaten together in 2+ meals, ranked by excess flares (togetherFlared - together * baseline), cap 12.
 */
export function computeSuspectOverlaps(
  meals: Meal[],
  ctx: FlareContext,
  ingredientNames: string[],
  groupNames: ContainsGroup[],
  memberGroups: Map<string, Set<ContainsGroup>>
): SuspectOverlap[] {
  const names = [...new Set([...ingredientNames, ...groupNames])];
  const groupSet = new Set<string>(groupNames);
  const rows = meals.map((m) => {
    const has = new Set<string>([...getMealIngredients(m).map((i) => i.name), ...getMealGroups(m)]);
    return { has, carriers: mealGroupCarriers(m), flared: mealFollowedByFlare(m, ctx).flared };
  });
  // One ingredient in the meal carries both groups.
  const oneCarrier = (r: (typeof rows)[number], x: string, y: string) => {
    const cy = r.carriers.get(y as ContainsGroup) ?? new Set<string>();
    for (const p of r.carriers.get(x as ContainsGroup) ?? []) if (cy.has(p)) return true;
    return false;
  };
  const nested = (x: string, y: string) => groupSet.has(y) && (memberGroups.get(x)?.has(y as ContainsGroup) ?? false);
  const out: SuspectOverlap[] = [];
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const [a, b] = [names[i], names[j]].sort((x, y) => x.localeCompare(y));
      if (nested(a, b) || nested(b, a)) continue;
      const bothGroups = groupSet.has(a) && groupSet.has(b);
      const o: SuspectOverlap = { a, b, together: 0, togetherFlared: 0, aWithoutB: 0, aWithoutBFlared: 0, bWithoutA: 0, bWithoutAFlared: 0 };
      for (const r of rows) {
        const ha = r.has.has(a);
        const hb = r.has.has(b);
        const f = r.flared ? 1 : 0;
        if (ha && hb && bothGroups && oneCarrier(r, a, b)) continue; // one food carries both
        if (ha && hb) { o.together++; o.togetherFlared += f; }
        else if (ha) { o.aWithoutB++; o.aWithoutBFlared += f; }
        else if (hb) { o.bWithoutA++; o.bWithoutAFlared += f; }
      }
      if (o.together >= 2) out.push({ ...o, ...labelOverlap(o, ctx.baselineRate) });
    }
  }
  // Rank by flares beyond what the baseline predicts for the shared meals, so that pairs with a very
  // common partner (eaten in most meals, flaring at the baseline rate) do not crowd out small, telling pairs.
  const excess = (o: SuspectOverlap) => o.togetherFlared - o.together * ctx.baselineRate;
  return out
    .sort(
      (x, y) =>
        excess(y) - excess(x) || y.together - x.together || x.a.localeCompare(y.a) || x.b.localeCompare(y.b)
    )
    .slice(0, OVERLAP_CAP);
}

// ---------- pair labels ----------
// All rates are flared / meals, compared with the baseline b (share of all meals followed by a flare).
/** "Clearly above" the baseline for an item eaten alone. */
export const ALONE_MARGIN = 0.15;
/** "Near" the baseline: an item alone at or below b + NEAR_MARGIN does not explain flares on its own. */
export const NEAR_MARGIN = 0.05;
/** "Clearly above" the baseline for a combination or a pattern. */
export const PATTERN_MARGIN = 0.2;
/** Minimum meals for a combination or pattern candidate. */
export const MIN_CANDIDATE_MEALS = 3;
/** Minimum flaring meals for a combination, a pattern, or the item that explains a bystander pair. */
export const MIN_FLARES = 3;
/** Minimum apart-meals on each side before that side's alone-rate is trusted. */
const MIN_APART = 3;
/** Minimum meals without the other item before an item can be judged a bystander. */
export const MIN_BYSTANDER_APART = 4;
/** z for the one-sided ~90% Wilson score lower bound. */
export const WILSON_Z = 1.28;
/** Candidates with fewer meals than this are marked "weak". */
export const SOLID_MIN_MEALS = 5;

/** Wilson score interval lower bound for k successes out of n (0 when n is 0). */
export function wilsonLower(k: number, n: number, z = WILSON_Z): number {
  if (n <= 0) return 0;
  const p = k / n;
  const z2 = z * z;
  return (p + z2 / (2 * n) - z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / (1 + z2 / n);
}

/** A rate that is clearly and reliably above the baseline: enough flares, a margin, and Wilson LB > b. */
export function clearlyAbove(flared: number, count: number, b: number): boolean {
  return (
    count >= MIN_CANDIDATE_MEALS &&
    flared >= MIN_FLARES &&
    flared / count >= b + PATTERN_MARGIN &&
    wilsonLower(flared, count) > b
  );
}

/**
 * Label a pair (first rule that matches wins):
 * 1. inseparable: either side was eaten without the other fewer than 3 times (that side's alone-rate
 *    cannot be judged; this also covers aWithoutB + bWithoutA < 3).
 * 2. combination: together is clearly above b (together >= 3, togetherFlared >= 3, rate >= b + 0.20 and
 *    Wilson lower bound (z 1.28) > b), and each alone-rate <= b + 0.05.
 * 3. independent: each alone-rate >= b + 0.15.
 * 4. bystander: X eaten without Y at least 4 times with alone-rate <= b; Y's alone-rate >= b + 0.15 with
 *    >= 3 flares and Wilson lower bound > b; and togetherFlared > 0. `bystander` names X.
 * 5. otherwise independent: no sign that either item depends on the other (judge each on its own stats).
 */
export function labelOverlap(o: SuspectOverlap, b: number): { label: OverlapLabel; bystander?: string } {
  if (o.aWithoutB < MIN_APART || o.bWithoutA < MIN_APART) return { label: 'inseparable' };
  const ra = o.aWithoutBFlared / o.aWithoutB;
  const rb = o.bWithoutAFlared / o.bWithoutA;
  if (clearlyAbove(o.togetherFlared, o.together, b) && ra <= b + NEAR_MARGIN && rb <= b + NEAR_MARGIN) {
    return { label: 'combination' };
  }
  if (ra >= b + ALONE_MARGIN && rb >= b + ALONE_MARGIN) return { label: 'independent' };
  const explains = (flared: number, n: number) =>
    flared / n >= b + ALONE_MARGIN && flared >= MIN_FLARES && wilsonLower(flared, n) > b;
  if (o.togetherFlared > 0) {
    if (o.aWithoutB >= MIN_BYSTANDER_APART && ra <= b && explains(o.bWithoutAFlared, o.bWithoutA)) {
      return { label: 'bystander', bystander: o.a };
    }
    if (o.bWithoutA >= MIN_BYSTANDER_APART && rb <= b && explains(o.aWithoutBFlared, o.aWithoutB)) {
      return { label: 'bystander', bystander: o.b };
    }
  }
  return { label: 'independent' };
}

// ---------- pattern candidates ----------
export const PATTERN_CONDITIONS = [
  'after 18:00',
  'after 20:00',
  '3+ consecutive days',
  'within 24h of a bowel movement gap',
] as const;
/** A pattern must also beat the item's own unconditioned rate by this much, or it is just the item. */
export const PATTERN_OVER_ITEM = 0.1;
const CANDIDATE_CAP = 8;

/**
 * Engine-verified candidates: every labelled combination pair (condition omitted), then each lead
 * (top ingredient or eaten group) x each condition template, counted by the verifier's own code.
 * Every candidate, combinations included, must pass `clearlyAbove` on the verifier's recount
 * (count >= 3, flared >= 3, rate >= b + 0.20, Wilson lower bound > b); a pattern must also reach the
 * item's plain rate + 0.10. strength is "weak" below 5 meals, else "solid".
 * Combinations come first; patterns fill the rest, ranked by excess flares (flared - count * b). Cap 8.
 */
export function computePatternCandidates(
  meals: Meal[],
  symptoms: Symptom[],
  ctx: FlareContext,
  leads: ItemStat[],
  overlaps: SuspectOverlap[]
): PatternCandidate[] {
  const b = ctx.baselineRate;
  const count = makePatternCounter(meals, symptoms, ctx);
  const excess = (c: PatternCandidate) => c.flared - c.count * b;
  const toCandidate = (items: string[], condition?: string): PatternCandidate | null => {
    const r = count(items, condition);
    if (!r || r.count === 0) return null;
    const c: PatternCandidate = {
      items,
      count: r.count,
      flared: r.flared,
      rate: Math.round((r.flared / r.count) * 100) / 100,
      evidenceDates: [...r.flaredDays].reverse().slice(0, 5),
    };
    if (condition) c.condition = condition;
    c.strength = r.count < SOLID_MIN_MEALS ? 'weak' : 'solid';
    return clearlyAbove(r.flared, r.count, b) ? c : null;
  };
  const byExcess = (x: PatternCandidate, y: PatternCandidate) =>
    excess(y) - excess(x) || y.count - x.count || x.items.join('|').localeCompare(y.items.join('|')) ||
    (x.condition ?? '').localeCompare(y.condition ?? '');

  const combos = overlaps
    .filter((o) => o.label === 'combination')
    .map((o) => toCandidate([o.a, o.b]))
    .filter((c): c is PatternCandidate => c !== null)
    .sort(byExcess);

  const patterns: PatternCandidate[] = [];
  for (const lead of leads) {
    if (lead.timesEaten === 0) continue;
    const plain = lead.timesFollowedByFlare / lead.timesEaten;
    for (const cond of PATTERN_CONDITIONS) {
      const c = toCandidate([lead.name], cond);
      if (c && c.flared / c.count >= plain + PATTERN_OVER_ITEM) patterns.push(c);
    }
  }
  patterns.sort(byExcess);
  return [...combos, ...patterns].slice(0, CANDIDATE_CAP);
}

// ---------- lead hints ----------
const pctText = (n: number, d: number) => `${Math.round((n / d) * 100)}%`;

/**
 * One line per lead (eaten groups, then ingredient leads), built only from the overlap labels:
 * - combination: "in a combination with X: flares mainly when eaten together"
 * - bystander (this lead named): "bystander alongside X: flares P% without it, at or below the baseline"
 * - bystander (the other named): "explains the flares it shares with X (X is the bystander)"
 * - independent: "independent of X (flares P% without it)"
 * - inseparable: "cannot yet be told apart from X"
 * A lead is "bystander only" when it is the bystander in at least one pair, is in no combination,
 * explains no pair, and is not independent with an alone-rate >= baseline + 0.15 in any pair.
 * The hint ends with "engine verdict: bystander only, reject" or "not a bystander in any pair" /
 * "not bystander-only: judge on its own numbers".
 * Firm verdicts (override the AI): reject = bystander only; keep = high confidence and not a
 * bystander in any pair. A firm keep hint ends with "engine verdict: keep".
 */
export function computeLeadHints(leads: ItemStat[], overlaps: SuspectOverlap[], baseline: number): LeadHint[] {
  return leads.map((lead) => {
    const name = lead.name;
    const parts: string[] = [];
    let bystanderIn = 0;
    let strongRole = false;
    for (const o of overlaps) {
      if (o.a !== name && o.b !== name) continue;
      const isA = o.a === name;
      const other = isA ? o.b : o.a;
      const alone = isA ? o.aWithoutB : o.bWithoutA;
      const aloneFlared = isA ? o.aWithoutBFlared : o.bWithoutAFlared;
      const rate = alone > 0 ? pctText(aloneFlared, alone) : 'n/a';
      switch (o.label) {
        case 'combination':
          parts.push(`in a combination with ${other}: flares mainly when eaten together`);
          strongRole = true;
          break;
        case 'bystander':
          if (o.bystander === name) {
            parts.push(`bystander alongside ${other}: flares ${rate} without it, at or below the baseline`);
            bystanderIn++;
          } else {
            parts.push(`explains the flares it shares with ${other} (${other} is the bystander)`);
            strongRole = true;
          }
          break;
        case 'independent':
          parts.push(`independent of ${other} (flares ${rate} without it)`);
          if (alone > 0 && aloneFlared / alone >= baseline + ALONE_MARGIN) strongRole = true;
          break;
        case 'inseparable':
          parts.push(`cannot yet be told apart from ${other}`);
          break;
      }
    }
    let firm: LeadHint['firm'];
    if (bystanderIn > 0 && !strongRole) firm = 'reject';
    else if (bystanderIn === 0 && lead.confidence === 'high') firm = 'keep';
    if (parts.length === 0) parts.push('no overlaps with other leads; judge on its own numbers');
    else if (firm === 'reject') parts.push('engine verdict: bystander only, reject');
    else if (bystanderIn === 0) parts.push('not a bystander in any pair');
    else parts.push('not bystander-only: judge on its own numbers');
    if (firm === 'keep') parts.push('engine verdict: keep');
    const hint: LeadHint = { name, hint: parts.join('; ') };
    if (firm) hint.firm = firm;
    return hint;
  });
}
